import type { useIntl } from "react-intl";
import type { DiffAction, ResolutionChoice } from "./profile-merge-types";

// Keep/use toggle labels, framed by the diff action (matching the previous
// preview modal): for an ADD the existing side is empty, so the choice reads
// "Skip" vs "Add"; for a CONFLICT it reads "Keep mine" vs "Use imported".
export function choiceOptions(
  intl: ReturnType<typeof useIntl>,
  action: DiffAction,
): { value: ResolutionChoice; label: string }[] {
  const isAdd = action === "ADD";
  return [
    {
      value: "KEEP_EXISTING",
      label: isAdd
        ? intl.formatMessage({ id: "profile.merge.skip", defaultMessage: "Skip" })
        : intl.formatMessage({ id: "profile.merge.keep", defaultMessage: "Keep mine" }),
    },
    {
      value: "USE_INCOMING",
      label: isAdd
        ? intl.formatMessage({ id: "profile.merge.add", defaultMessage: "Add" })
        : intl.formatMessage({ id: "profile.merge.use", defaultMessage: "Use imported" }),
    },
  ];
}

// Turns a raw camelCase field key into a sentence-case fallback label
// (jobTitle → "Job title"). Used only when no i18n key exists for the key.
export function humanizeLabel(field: string): string {
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toUpperCase() + word.slice(1)
        : word.charAt(0).toLowerCase() + word.slice(1),
    )
    .join(" ");
}

// The field key genesis sends in a collection-row diff → the SAME label the
// normal profile editor shows for that field, so a field reads identically
// whether the user edits it inline or during a merge (e.g. "Job title", not
// "Title"; "Main tasks", not "Description"). Reuses the `profile.page.field.*`
// message ids rather than a parallel label set, so the two never drift.
const PROFILE_FIELD_LABEL: Record<string, { id: string; defaultMessage: string }> = {
  // experience
  title: { id: "profile.page.field.jobTitle.label", defaultMessage: "Job title" },
  employer: { id: "profile.page.field.employer.label", defaultMessage: "Employer" },
  location: { id: "profile.page.field.location", defaultMessage: "Location" },
  startDate: { id: "profile.page.field.from", defaultMessage: "From" },
  endDate: { id: "profile.page.field.to", defaultMessage: "To" },
  description: { id: "profile.page.field.activities.label", defaultMessage: "Main tasks" },
  // education
  institution: { id: "profile.page.field.institution", defaultMessage: "Educational institution" },
  degree: { id: "profile.page.field.degree.label", defaultMessage: "Degree" },
  fieldOfStudy: { id: "profile.page.field.field.label", defaultMessage: "Field of study" },
  // certificate
  name: { id: "profile.page.field.certificateName.label", defaultMessage: "Name" },
  issued: { id: "profile.page.field.issueDate", defaultMessage: "Issue date" },
  // language
  language: { id: "profile.page.field.language.label", defaultMessage: "Language" },
  level: { id: "profile.page.field.cefr", defaultMessage: "Level (CEFR)" },
  // skills
  skill: { id: "profile.page.field.expertiseName.label", defaultMessage: "Name" },
};

// The field key genesis sends (title, employer, institution, …) is a machine
// key; its user-facing label matches the profile editor for known fields, else
// falls back to `linkedin.import.field.<key>` / the humanized English form for
// an unknown/newer key.
export function fieldLabel(intl: ReturnType<typeof useIntl>, field: string): string {
  const profileLabel = PROFILE_FIELD_LABEL[field];
  if (profileLabel) return intl.formatMessage(profileLabel);
  return intl.formatMessage({
    id: `linkedin.import.field.${field}`,
    defaultMessage: humanizeLabel(field),
  });
}

// A BCP-47 language tag (genesis sends the tag as the language value) rendered as
// its full, locale-aware name; falls back to the raw tag for an unknown tag.
export function languageDisplayName(tag: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "language" }).of(tag) ?? tag;
  } catch {
    return tag;
  }
}
